import { Model, modelRegistry } from './Model';
import SyncData from './SyncData';
import { Table } from '../models';
import { TableSessionStatusType } from '../types';
import { apiService, connectionService } from '../services';
import { Store } from '../wrappers';
import { HashGen } from '../utilities';
export class TableSession extends Model {
    static entity = 'table_sessions';
    static primaryKey = 'hash';
    static syncable = true;
    id = 0;
    hash = '';
    table_id = 0;
    tenant_id = 0;
    pin = '';
    status = TableSessionStatusType.active;
    opened_by = 0;
    opened_at = '';
    closed_at = null;
    expires_at = null;
    created_at = '';
    updated_at = '';
    constructor(record) {
        super();
        this.$fill(record);
    }
    static fields() {
        return {
            id: this.attr(0),
            hash: this.attr(HashGen.long()),
            table_id: this.attr(0),
            tenant_id: this.attr(0),
            pin: this.attr(''),
            status: this.attr(TableSessionStatusType.active),
            opened_by: this.attr(0),
            opened_at: this.attr(''),
            closed_at: this.attr(null),
            expires_at: this.attr(null),
            created_at: this.attr(''),
            updated_at: this.attr(''),
        };
    }
    // --- Insert with reconciliation ---
    /**
     * Insert with reconciliation: when an authoritative (server-known) active
     * session arrives for a table, any never-synced local duplicate (id === 0,
     * different hash) on the same table lost the race — drop it so
     * forTable() can't surface a PIN the server never accepted.
     */
    static insert(payload) {
        super.insert(payload);
        for (const record of payload.data || []) {
            if (!record || record.status !== TableSessionStatusType.active || !record.id)
                continue;
            TableSession.all()
                .filter(s => s.table_id === record.table_id
                && s.hash !== record.hash
                && s.status === TableSessionStatusType.active
                && !s.id)
                .forEach(s => s.$delete());
        }
    }
    // --- Queries ---
    /** Get the active session for a table, or undefined */
    static forTable(tableId) {
        return TableSession.all().find(s => s.table_id === tableId && s.status === TableSessionStatusType.active);
    }
    // --- Sync ---
    async sync(action) {
        const apiAction = action === 'create' ? 'post' : 'patch';
        const record = this.$toJson();
        await this.markForSync(action);
        this.$save();
        if (!Store.get('isOnline')) {
            TableSession.syncTerminals({ items: [{ action: apiAction, record }] });
            return { success: true };
        }
        try {
            const url = action === 'create' ? '/table-sessions'
                : action === 'transfer' ? `/table-sessions/${this.hash}/transfer`
                    : `/table-sessions/${this.hash}/close`;
            const method = action === 'create' ? 'post' : 'patch';
            const payload = action === 'create' ? { table_id: this.table_id, hash: this.hash, pin: this.pin }
                : action === 'transfer' ? { table_id: this.table_id }
                    : {};
            const response = await apiService[method](url, payload);
            if (response.data.table_session) {
                this.$fill(response.data.table_session);
                TableSession.insert({ data: [response.data.table_session] });
            }
            TableSession.syncTerminals({ items: [{ action: 'patch', record: this.$toJson() }] });
            await this.removeFromSync();
            return { success: true };
        }
        catch (e) {
            // 409 on create: session already exists — another terminal opened one for this table
            if (action === 'create' && e?.response?.status === 409 && e.response.data?.table_session) {
                await this.removeFromSync();
                const staleHash = this.hash;
                this.$fill(e.response.data.table_session);
                this.$save();
                // Clean up stale local record (different hash = different primaryKey = separate store entry)
                if (staleHash !== this.hash) {
                    const stale = TableSession.all().find(s => s.hash === staleHash);
                    if (stale)
                        stale.$delete();
                }
                return { success: true };
            }
            // 409 on transfer: destination got an active session meanwhile — merge
            // semantics: destination wins, this (source) session closes instead.
            // (table_id already points at the destination; harmless on a closed record.)
            if (action === 'transfer' && e?.response?.status === 409 && e.response.data?.table_session) {
                await this.removeFromSync();
                TableSession.insert({ data: [e.response.data.table_session] });
                this.status = TableSessionStatusType.closed;
                this.closed_at = new Date().toISOString();
                return this.sync('update');
            }
            // 404 on close/transfer: session already closed on another terminal
            if ((action === 'update' || action === 'transfer') && e?.response?.status === 404) {
                // 404 on transfer of a session created OFFLINE: the server has
                // never seen it — its 'create' is still queued. Closing here
                // would also purge that pending create (removeFromSync deletes
                // by record hash across ALL queue entries), killing the
                // session. Leave the queued create + transfer to replay in
                // order later and self-heal.
                if (action === 'transfer') {
                    const pendingCreate = (await SyncData.getDataForSync()).some(sd => sd.type === 'TableSession'
                        && sd.action === 'create'
                        && sd.data.some(record => record.hash === this.hash));
                    if (pendingCreate) {
                        TableSession.syncTerminals({ items: [{ action: 'patch', record: this.$toJson() }] });
                        return { success: true };
                    }
                }
                this.status = TableSessionStatusType.closed;
                this.$save();
                await this.removeFromSync();
                return { success: true };
            }
            const isOnline = await connectionService.connectionCheckRetry(5000);
            return { success: isOnline };
        }
    }
    // --- PIN generation ---
    /** Generate a 4-digit PIN unique among active local sessions, avoiding trivially guessable sequences */
    static generatePin() {
        const forbidden = [
            '0000', '1111', '2222', '3333', '4444',
            '5555', '6666', '7777', '8888', '9999',
            '1234', '4321',
        ];
        const activePins = TableSession.all()
            .filter(s => s.status === TableSessionStatusType.active)
            .map(s => s.pin);
        let pin;
        do {
            const arr = new Uint16Array(1);
            crypto.getRandomValues(arr);
            pin = String(arr[0] % 10000).padStart(4, '0');
        } while (forbidden.includes(pin) || activePins.includes(pin));
        return pin;
    }
    // --- Instance helpers ---
    isActive() {
        return this.status === TableSessionStatusType.active;
    }
    getTable() {
        return Table.find(this.table_id);
    }
}
export default TableSession;
modelRegistry.register('TableSession', TableSession);
//# sourceMappingURL=TableSession.js.map